// Schweizer Steuerfuss-Daten (ungefähre Werte für Gemeinde + Kanton)
// Quelle: Verschiedene kantonale Steuerämter, Stand 2024

export interface TaxLocation {
  city: string;
  canton: string;
  municipalTaxRate: number; // Gemeindesteuerfuss in %
  cantonalTaxRate: number;  // Kantonaler Steuerfuss in %
  totalTaxRate: number;     // Kombinierter Steuerfuss in %
}

export const swissTaxData: TaxLocation[] = [
  // Zürich - Korrigierte Steuerfüsse (Steuerfuss-Einheiten, nicht Prozent)
  { city: "Zürich", canton: "ZH", municipalTaxRate: 119, cantonalTaxRate: 100, totalTaxRate: 219 },
  { city: "Winterthur", canton: "ZH", municipalTaxRate: 122, cantonalTaxRate: 100, totalTaxRate: 222 },
  { city: "Uster", canton: "ZH", municipalTaxRate: 107, cantonalTaxRate: 100, totalTaxRate: 207 },
  { city: "Dübendorf", canton: "ZH", municipalTaxRate: 105, cantonalTaxRate: 100, totalTaxRate: 205 },
  { city: "Dietikon", canton: "ZH", municipalTaxRate: 109, cantonalTaxRate: 100, totalTaxRate: 209 },
  
  // Bern
  { city: "Bern", canton: "BE", municipalTaxRate: 154, cantonalTaxRate: 306, totalTaxRate: 460 },
  { city: "Biel", canton: "BE", municipalTaxRate: 160, cantonalTaxRate: 306, totalTaxRate: 466 },
  { city: "Thun", canton: "BE", municipalTaxRate: 140, cantonalTaxRate: 306, totalTaxRate: 446 },
  { city: "Köniz", canton: "BE", municipalTaxRate: 135, cantonalTaxRate: 306, totalTaxRate: 441 },
  
  // Basel
  { city: "Basel", canton: "BS", municipalTaxRate: 0, cantonalTaxRate: 220, totalTaxRate: 220 },
  { city: "Riehen", canton: "BS", municipalTaxRate: 0, cantonalTaxRate: 220, totalTaxRate: 220 },
  { city: "Allschwil", canton: "BL", municipalTaxRate: 56, cantonalTaxRate: 220, totalTaxRate: 276 },
  { city: "Muttenz", canton: "BL", municipalTaxRate: 57, cantonalTaxRate: 220, totalTaxRate: 277 },
  
  // Genf
  { city: "Genf", canton: "GE", municipalTaxRate: 45.5, cantonalTaxRate: 169.5, totalTaxRate: 215 },
  { city: "Vernier", canton: "GE", municipalTaxRate: 45.5, cantonalTaxRate: 169.5, totalTaxRate: 215 },
  { city: "Lancy", canton: "GE", municipalTaxRate: 45.5, cantonalTaxRate: 169.5, totalTaxRate: 215 },
  
  // Lausanne
  { city: "Lausanne", canton: "VD", municipalTaxRate: 79, cantonalTaxRate: 154, totalTaxRate: 233 },
  { city: "Yverdon", canton: "VD", municipalTaxRate: 75, cantonalTaxRate: 154, totalTaxRate: 229 },
  { city: "Montreux", canton: "VD", municipalTaxRate: 60, cantonalTaxRate: 154, totalTaxRate: 214 },
  
  // Luzern
  { city: "Luzern", canton: "LU", municipalTaxRate: 150, cantonalTaxRate: 190, totalTaxRate: 340 },
  { city: "Emmen", canton: "LU", municipalTaxRate: 160, cantonalTaxRate: 190, totalTaxRate: 350 },
  { city: "Kriens", canton: "LU", municipalTaxRate: 155, cantonalTaxRate: 190, totalTaxRate: 345 },
  
  // St. Gallen
  { city: "St. Gallen", canton: "SG", municipalTaxRate: 129, cantonalTaxRate: 125, totalTaxRate: 254 },
  { city: "Rapperswil", canton: "SG", municipalTaxRate: 110, cantonalTaxRate: 125, totalTaxRate: 235 },
  { city: "Wil", canton: "SG", municipalTaxRate: 125, cantonalTaxRate: 125, totalTaxRate: 250 },
  
  // Aargau
  { city: "Aarau", canton: "AG", municipalTaxRate: 103, cantonalTaxRate: 109, totalTaxRate: 212 },
  { city: "Baden", canton: "AG", municipalTaxRate: 101, cantonalTaxRate: 109, totalTaxRate: 210 },
  { city: "Wettingen", canton: "AG", municipalTaxRate: 98, cantonalTaxRate: 109, totalTaxRate: 207 },
  
  // Weitere wichtige Städte
  { city: "Lugano", canton: "TI", municipalTaxRate: 75, cantonalTaxRate: 125, totalTaxRate: 200 },
  { city: "Chur", canton: "GR", municipalTaxRate: 125, cantonalTaxRate: 128, totalTaxRate: 253 },
  { city: "Neuchâtel", canton: "NE", municipalTaxRate: 68, cantonalTaxRate: 180, totalTaxRate: 248 },
  { city: "Fribourg", canton: "FR", municipalTaxRate: 100, cantonalTaxRate: 178, totalTaxRate: 278 },
  { city: "Schaffhausen", canton: "SH", municipalTaxRate: 118, cantonalTaxRate: 125, totalTaxRate: 243 },
  { city: "Zug", canton: "ZG", municipalTaxRate: 64, cantonalTaxRate: 76, totalTaxRate: 140 },
  { city: "Schwyz", canton: "SZ", municipalTaxRate: 100, cantonalTaxRate: 77, totalTaxRate: 177 }
];

// Funktion um Steuerfuss basierend auf Stadt zu finden
export function getTaxRateByCity(cityName: string): TaxLocation | null {
  const normalizedInput = cityName.toLowerCase().trim();
  
  return swissTaxData.find(location => 
    location.city.toLowerCase() === normalizedInput ||
    location.city.toLowerCase().includes(normalizedInput) ||
    normalizedInput.includes(location.city.toLowerCase())
  ) || null;
}

// Funktion um alle verfügbaren Städte zu bekommen
export function getAllCities(): string[] {
  return swissTaxData.map(location => `${location.city}, ${location.canton}`).sort();
}

// Funktion um Steuerbetrag zu berechnen
export function calculateTaxAmount(grossIncome: number, cityName: string): {
  taxRate: number;
  estimatedTax: number;
  location: TaxLocation | null;
  details: {
    taxableIncome: number;
    deductions: number;
    effectiveRate: number;
  };
} {
  const location = getTaxRateByCity(cityName);
  
  if (!location) {
    // Fallback: Durchschnittlicher Schweizer Steuerfuss
    const standardDeductions = Math.min(grossIncome * 0.15, 20000);
    const taxableIncome = Math.max(0, grossIncome - standardDeductions);
    const estimatedTax = calculateSwissTax(taxableIncome, 250); // Durchschnittlicher Steuerfuss
    
    return {
      taxRate: 250 / 100,
      estimatedTax,
      location: null,
      details: {
        taxableIncome,
        deductions: standardDeductions,
        effectiveRate: grossIncome > 0 ? (estimatedTax / grossIncome) * 100 : 0
      }
    };
  }
  
  // Realistische Berechnung mit Standardabzügen
  const deductions = calculateStandardDeductions(grossIncome, location.canton);
  const taxableIncome = Math.max(0, grossIncome - deductions);
  
  // Schweizer Steuerberechnung mit korrekten Steuerfuss-Einheiten
  const estimatedTax = calculateSwissTax(taxableIncome, location.totalTaxRate);
  
  const effectiveRate = grossIncome > 0 ? (estimatedTax / grossIncome) * 100 : 0;
  
  return {
    taxRate: location.totalTaxRate / 100,
    estimatedTax,
    location,
    details: {
      taxableIncome,
      deductions,
      effectiveRate
    }
  };
}

// Berechnung der Standardabzüge (realistischer für Schweiz)
function calculateStandardDeductions(grossIncome: number, canton: string): number {
  let totalDeductions = 0;
  
  // Berufskosten-Abzug (je nach Kanton unterschiedlich)
  let berufskostenAbzug = 0;
  switch (canton) {
    case 'ZH':
      berufskostenAbzug = Math.min(grossIncome * 0.03, 4000); // Zürich: 3% bis max. 4'000
      break;
    case 'BE':
      berufskostenAbzug = Math.min(grossIncome * 0.03, 3000); // Bern: 3% bis max. 3'000
      break;
    case 'VD':
    case 'GE':
      berufskostenAbzug = Math.min(grossIncome * 0.03, 3500);
      break;
    case 'ZG':
    case 'SZ':
      berufskostenAbzug = Math.min(grossIncome * 0.02, 2500); // Tiefsteuer-Kantone
      break;
    default:
      berufskostenAbzug = Math.min(grossIncome * 0.03, 3000);
  }
  
  // Versicherungsabzug (AHV/IV/EO/ALV ca. 6.4% + BVG ca. 8.5% = ca. 15%)
  const sozialversicherungAbzug = grossIncome * 0.144; // 14.4% für Sozialversicherungen
  
  // Säule 3a Abzug (ca. 7'056 CHF pro Jahr für Erwerbstätige)
  const saule3aAbzug = Math.min(7056, grossIncome * 0.1);
  
  // Allgemeine Abzüge (Grundabzug)
  let grundabzug = 0;
  switch (canton) {
    case 'ZH':
      grundabzug = 2400; // Personalabzug Zürich 2024
      break;
    case 'BE':
      grundabzug = 2000;
      break;
    case 'VD':
    case 'GE':
      grundabzug = 1800;
      break;
    case 'ZG':
    case 'SZ':
      grundabzug = 1200;
      break;
    default:
      grundabzug = 2000;
  }
  
  totalDeductions = berufskostenAbzug + sozialversicherungAbzug + saule3aAbzug + grundabzug;
  
  // Mindestens 15% des Bruttoeinkommens (realistische Abzüge), maximal 35%
  return Math.max(
    Math.min(totalDeductions, grossIncome * 0.35), 
    grossIncome * 0.15
  );
}

// Schweizer Steuerberechnung mit korrekten Steuerfuss-Einheiten
function calculateSwissTax(taxableIncome: number, steuerfuss: number): number {
  if (taxableIncome <= 0) return 0;
  
  // Schweizer Steuerfuss wird auf die "einfache Steuer" angewendet
  // Die "einfache Steuer" basiert auf progressiven Tarifen
  
  // Vereinfachter progressiver Tarif für die einfache Steuer (Zürich als Beispiel)
  let einfacheSteuer = 0;
  
  // Progressive Tarif-Stufen (vereinfacht)
  if (taxableIncome <= 14500) {
    einfacheSteuer = 0; // Steuerfreier Grundbetrag
  } else if (taxableIncome <= 31600) {
    // 1. Stufe: ca. 0.77% Progression
    const progressiveBand = taxableIncome - 14500;
    einfacheSteuer = progressiveBand * 0.0077;
  } else if (taxableIncome <= 41400) {
    // Bis 31'600: ca. 132 CHF
    einfacheSteuer = 132 + (taxableIncome - 31600) * 0.088;
  } else if (taxableIncome <= 55200) {
    // Bis 41'400: ca. 994 CHF
    einfacheSteuer = 994 + (taxableIncome - 41400) * 0.11;
  } else if (taxableIncome <= 72500) {
    // Bis 55'200: ca. 2'512 CHF
    einfacheSteuer = 2512 + (taxableIncome - 55200) * 0.13;
  } else if (taxableIncome <= 78100) {
    // Bis 72'500: ca. 4'761 CHF
    einfacheSteuer = 4761 + (taxableIncome - 72500) * 0.175;
  } else {
    // Über 78'100: Höchstsatz
    einfacheSteuer = 5741 + (taxableIncome - 78100) * 0.13;
  }
  
  // Totale Steuer = einfache Steuer × Steuerfuss (in %)
  const totaleTax = einfacheSteuer * (steuerfuss / 100);
  
  return Math.round(totaleTax);
}

// Städte gruppiert nach Kantonen für bessere UX
export const citiesByCantons = swissTaxData.reduce((acc, location) => {
  if (!acc[location.canton]) {
    acc[location.canton] = [];
  }
  acc[location.canton].push(location);
  return acc;
}, {} as Record<string, TaxLocation[]>);